import { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { agentService } from '@/services';
import { useContextStore } from '@app/store/context.store';
import { useAuthStore } from '@app/store/auth.store';
import type { Agent } from '@/services/agent.service';
import type { AgentFormValues } from '../types';
import { isApiError } from '@core/api/types';
import { can, canAgent } from '@features/auth/roles';

export type { Agent };

function toastError(err: unknown, fallback: string) {
  // Expected for users without agents.read — callers should gate with `enabled`.
  if (isApiError(err) && err.status === 403) return;
  const message = isApiError(err) ? err.message : fallback;
  toast.error(message);
}

/**
 * API-connected agent CRUD hook.
 *
 * Reads the active tenantId from ContextStore — no prop needed.
 * Uses GET /agents (tenant from x-tenant-id), then filters by agent grants.
 *
 * - `loading`    → initial list fetch in progress
 * - `submitting` → create / update / remove in progress
 */
function mergeAgentListItem(existing: Agent, updated: Agent): Agent {
  return {
    ...existing,
    ...updated,
    tenantName: updated.tenantName ?? existing.tenantName,
    documentCount: updated.documentCount ?? existing.documentCount,
    conversationCount: updated.conversationCount ?? existing.conversationCount,
  };
}

export function useAgents(options?: { enabled?: boolean }) {
  const enabled = options?.enabled !== false;
  const user = useAuthStore((s) => s.user);
  const { activeTenantId, activeTenant, setActiveAgent } = useContextStore();

  const [agents, setAgents] = useState<Agent[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const canList =
    enabled &&
    Boolean(activeTenantId) &&
    (can(user, 'tenant.agents.read') ||
      Object.keys(user?.agentGrants ?? {}).length > 0);

  // ── Fetch ─────────────────────────────────────────────────────────────────

  const fetchAll = useCallback(async (options?: { silent?: boolean }) => {
    if (!activeTenantId || !canList) {
      setAgents([]);
      return;
    }
    if (!options?.silent) setLoading(true);
    try {
      // Tenant-scoped via x-tenant-id header (not the platform-only nested route).
      const items = await agentService.listAll();
      const scoped = items
        .filter((agent) => canAgent(user, agent.id, 'tenant.agents.read'))
        .map((agent) => ({
          ...agent,
          tenantName: agent.tenantName ?? activeTenant?.name,
        }));
      setAgents(scoped);
    } catch (err) {
      setAgents([]);
      toastError(err, 'Failed to load agents');
    } finally {
      if (!options?.silent) setLoading(false);
    }
  }, [activeTenantId, activeTenant?.name, canList, user]);

  useEffect(() => {
    setAgents([]);
    void fetchAll();
  }, [fetchAll]);

  // ── Create ────────────────────────────────────────────────────────────────

  const create = useCallback(
    async (values: AgentFormValues): Promise<void> => {
      if (!activeTenantId) {
        toast.error('Select a tenant before creating an agent.');
        return;
      }
      setSubmitting(true);
      try {
        const agent = await agentService.create({
          name: values.name,
          slug: values.slug,
          description: values.description || undefined,
          systemPrompt: values.systemPrompt,
          model: values.model,
          isActive: values.isActive,
          modelConfig: values.modelConfig,
          retrievalConfig: values.retrievalConfig,
        });
        setAgents((prev) => [
          { ...agent, tenantName: agent.tenantName ?? activeTenant?.name },
          ...prev,
        ]);
        toast.success(`Agent "${agent.name}" created`);
      } catch (err) {
        toastError(err, 'Failed to create agent');
        throw err;
      } finally {
        setSubmitting(false);
      }
    },
    [activeTenantId, activeTenant?.name],
  );

  // ── Update ────────────────────────────────────────────────────────────────

  const update = useCallback(
    async (id: string, values: Partial<AgentFormValues>): Promise<void> => {
      setSubmitting(true);
      try {
        const agent = await agentService.update(id, {
          name: values.name,
          slug: values.slug,
          description: values.description || undefined,
          systemPrompt: values.systemPrompt,
          model: values.model,
          isActive: values.isActive,
          modelConfig: values.modelConfig,
          retrievalConfig: values.retrievalConfig,
        });
        setAgents((prev) =>
          prev.map((a) => (a.id === id ? mergeAgentListItem(a, agent) : a)),
        );
        const { activeAgentId } = useContextStore.getState();
        if (activeAgentId === id) setActiveAgent(agent);
        toast.success(`Agent "${agent.name}" updated`);
      } catch (err) {
        toastError(err, 'Failed to update agent');
        throw err;
      } finally {
        setSubmitting(false);
      }
    },
    [setActiveAgent],
  );

  // ── Update system prompt ──────────────────────────────────────────────────

  const updateSystemPrompt = useCallback(
    async (id: string, systemPrompt: string): Promise<void> => {
      setSubmitting(true);
      try {
        const agent = await agentService.updateSystemPrompt(id, {
          systemPrompt,
        });
        setAgents((prev) =>
          prev.map((a) => (a.id === id ? mergeAgentListItem(a, agent) : a)),
        );
        const { activeAgentId } = useContextStore.getState();
        if (activeAgentId === id) setActiveAgent(agent);
        toast.success('System prompt saved');
      } catch (err) {
        toastError(err, 'Failed to save system prompt');
        throw err;
      } finally {
        setSubmitting(false);
      }
    },
    [setActiveAgent],
  );

  // ── Remove (optimistic) ───────────────────────────────────────────────────

  const remove = useCallback(async (id: string): Promise<void> => {
    const snapshot: Agent[] = [];
    setAgents((prev) => {
      snapshot.push(...prev);
      return prev.filter((a) => a.id !== id);
    });

    const { activeAgentId, clearActiveAgent } = useContextStore.getState();
    if (activeAgentId === id) clearActiveAgent();

    try {
      await agentService.delete(id);
      toast.success('Agent deleted');
    } catch (err) {
      setAgents(snapshot);
      toastError(err, 'Failed to delete agent');
      throw err;
    }
  }, []);

  return {
    agents,
    loading,
    submitting,
    activeTenantId,
    create,
    update,
    updateSystemPrompt,
    remove,
    refetch: fetchAll,
  };
}
